import type { DocumentKind } from './files';

export type ExtractResult =
  | { ok: true; kind: DocumentKind; text: string }
  | { ok: false; kind: DocumentKind; error: string };

const MAX_PDF_PAGES = 60;

class PdfPageLimitError extends Error {
  constructor(fileName: string, pages: number) {
    super(
      `"${fileName}" has ${pages} pages — over the ${MAX_PDF_PAGES}-page limit for CVs. Paste the relevant text instead.`,
    );
  }
}

/**
 * Extracts plain text from an already-validated, untrusted document entirely
 * in the browser. Only text content is ever returned — no HTML, no rendered
 * markup, no network access, and nothing is persisted.
 */
export async function extractTextFromFile(
  file: File,
  kind: DocumentKind,
): Promise<ExtractResult> {
  try {
    let text: string;
    if (kind === 'pdf') {
      text = await extractPdfText(file);
    } else if (kind === 'docx') {
      text = await extractDocxText(file);
    } else {
      text = await file.text();
    }

    const trimmed = text.trim();
    if (trimmed.length === 0) {
      return {
        ok: false,
        kind,
        error:
          kind === 'pdf'
            ? `No selectable text found in "${file.name}". It is probably a scanned or image-only PDF — OCR is not supported yet, so please paste the text manually or upload a text-based PDF.`
            : `"${file.name}" contains no readable text. Add content or paste your CV directly into the field.`,
      };
    }

    return { ok: true, kind, text: trimmed };
  } catch (error) {
    if (error instanceof PdfPageLimitError) {
      return { ok: false, kind, error: error.message };
    }
    return {
      ok: false,
      kind,
      error: `Could not read "${file.name}". The file may be corrupt or use features this browser parser does not support.`,
    };
  }
}

async function extractPdfText(file: File): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

  const data = new Uint8Array(await file.arrayBuffer());
  const loadingTask = pdfjs.getDocument({ data });
  const doc = await loadingTask.promise;
  try {
    if (doc.numPages > MAX_PDF_PAGES) {
      throw new PdfPageLimitError(file.name, doc.numPages);
    }
    const lines: string[] = [];
    for (let page = 1; page <= doc.numPages; page += 1) {
      const { items } = await (await doc.getPage(page)).getTextContent();
      for (const item of items) {
        if (typeof item === 'object' && item !== null && 'str' in item) {
          const str = String((item as { str: unknown }).str);
          const eol =
            'hasEOL' in item && (item as { hasEOL: unknown }).hasEOL === true;
          lines.push(eol ? `${str}\n` : str);
        }
      }
      lines.push('\n');
    }
    return lines.join('');
  } finally {
    await loadingTask.destroy();
  }
}

async function extractDocxText(file: File): Promise<string> {
  const mammoth = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}
